/**
 * 配信の後に開いたままの会員の画面を、新しいビルドへ切り替えるための見張り（docs/08 §4.6）。
 *
 * <b>画面の中で移るだけでは JS を読み直さない</b>——配信の前から開いていたタブは、古い JS のまま新しい API と話し、
 * 食い違った画面を出す。書き出しが <c>/app/version.json</c> にビルドの ID（member.js の中身の版。外枠が <c>?v=</c> に付けるもの）を書き、
 * ここで今動いている JS の ID と比べる。違えば、次に別の画面へ移るときにページごと読み込む（<c>app/build-version-watch.tsx</c>）。
 *
 * <b>確かめられないときは何もしない</b>（開発のサーバ・version.json が無い・読めない）。画面を壊さない。
 */

/** ビルドの ID を書いたファイル（書き出し先からの URL）。<b>書き出しも同じ値を使う</b>（<c>SiteApp/build/render-site.ts</c>）。 */
export const buildVersionPath = "/app/version.json";

/** 読みに行く間隔の下限。<b>前回から 60 秒以内なら読まない</b>（画面を移るたびに読みに行かない）。 */
export const checkInterval = 60_000;

/**
 * 読み込んだ JS の URL から、ビルドの ID（<c>?v=</c> の値）を取り出す。
 * <b>付いていなければ null</b>（Vite の開発サーバなど）——そのときは見張らない。
 */
export const buildIdOf = (scriptUrl: string): string | null => {
    try {
        const id = new URL(scriptUrl).searchParams.get("v");
        return id === null || id === "" ? null : id;
    } catch {
        return null;
    }
};

/** version.json の中身（<c>{ "id": "…" }</c>）から ID を取り出す。形が違えば null。 */
export const parseBuildVersion = (value: unknown): string | null => {
    if (typeof value !== "object" || value === null) {
        return null;
    }
    const id = (value as { readonly id?: unknown }).id;
    return typeof id === "string" && id !== "" ? id : null;
};

/** 配っているビルドの ID を読む。<b>読めなければ null</b>（404・形が違う・通信の失敗）。 */
export const loadBuildVersion = async (): Promise<string | null> => {
    try {
        const response = await fetch(buildVersionPath, { cache: "no-store" });
        return response.ok ? parseBuildVersion(await response.json()) : null;
    } catch {
        return null;
    }
};

export interface VersionWatch {
    /** 配っている ID を読みに行く（前回から <c>checkInterval</c> 以内なら読まない）。結果は待たない。 */
    readonly check: () => void;
    /** 配っている ID が今の JS と違うと分かったか。 */
    readonly stale: () => boolean;
}

export interface VersionWatchOptions {
    /** 今動いている JS の ID（<c>buildIdOf(import.meta.url)</c>）。null なら見張らない。 */
    readonly currentId: string | null;
    /** 配っている ID を読む（テストで差し替える）。 */
    readonly load?: () => Promise<string | null>;
    /** 今の時刻（ミリ秒。テストで差し替える）。 */
    readonly now?: () => number;
}

/**
 * 見張りを作る。<b>今の JS の ID が分からなければ null</b>（何もしない）。
 * 開いた直後は読んだばかりなので、最初の読み込みも作ったときから <c>checkInterval</c> を空ける。
 */
export function createVersionWatch(options: VersionWatchOptions): VersionWatch | null {
    const { currentId } = options;
    if (currentId === null) {
        return null;
    }
    const load = options.load ?? loadBuildVersion;
    const now = options.now ?? Date.now;

    let last = now();
    let loading = false;
    let stale = false;

    const compare = async (): Promise<void> => {
        loading = true;
        try {
            // 読めなかった（null）ときは何もしない。違うと分かったときだけ切り替える
            const id = await load();
            if (id !== null && id !== currentId) {
                stale = true;
            }
        } catch {
            // 読めなければ何もしない
        } finally {
            loading = false;
        }
    };

    return {
        check: () => {
            const time = now();
            if (stale || loading || time - last < checkInterval) {
                return;
            }
            last = time;
            void compare();
        },
        stale: () => stale,
    };
}
